import { useCallback, useEffect, useMemo, useState } from "react";

import {
  ORDER_TICKET_DRAWER_WIDTH,
  ORDER_TICKET_PUSH_QUERY,
  type OrderTicketDraft,
  REDUCED_MOTION_QUERY,
  type WorkspaceEngine,
} from "./OrderTicketDrawer.types";

const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState(
    () => window.matchMedia(query).matches,
  );

  useEffect(() => {
    const media = window.matchMedia(query);
    const onChange = () => setMatches(media.matches);
    media.addEventListener("change", onChange);
    onChange();
    return () => media.removeEventListener("change", onChange);
  }, [query]);

  return matches;
};

export interface OrderTicketDrawerControllerValue {
  engine: WorkspaceEngine;
  open: boolean;
  draft: OrderTicketDraft;
  sessionRevision: number;
  modal: boolean;
  reservedWidth: string;
  transition: string;
  /** Drives both the column transition and the drawer's own slide animation. */
  reducedMotion: boolean;
  openOrderTicket: (draft?: OrderTicketDraft) => void;
  closeOrderTicket: () => void;
  /** Chrome toggle: opens an empty ticket when closed, closes when open. */
  toggleOrderTicket: () => void;
}

export const useOrderTicketDrawerController = ({
  engine,
}: {
  engine: WorkspaceEngine;
}): OrderTicketDrawerControllerValue => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<OrderTicketDraft>({});
  const [sessionRevision, setSessionRevision] = useState(0);
  const pushMode = useMediaQuery(ORDER_TICKET_PUSH_QUERY);
  const reducedMotion = useMediaQuery(REDUCED_MOTION_QUERY);

  const openOrderTicket = useCallback((nextDraft: OrderTicketDraft = {}) => {
    setDraft({
      ...nextDraft,
      ...(nextDraft.legs ? { legs: [...nextDraft.legs] } : {}),
    });
    setOpen(true);
    setSessionRevision((revision) => revision + 1);
  }, []);

  const closeOrderTicket = useCallback(() => {
    setOpen(false);
    setDraft({});
  }, []);

  const toggleOrderTicket = useCallback(() => {
    setOpen((current) => {
      if (current) {
        setDraft({});
        return false;
      }
      setDraft({});
      setSessionRevision((revision) => revision + 1);
      return true;
    });
  }, []);

  const modal = !pushMode;
  const reservedWidth = open && pushMode ? ORDER_TICKET_DRAWER_WIDTH : "0px";
  const transition = reducedMotion
    ? "none"
    : "grid-template-columns 200ms ease";

  return useMemo(
    () => ({
      engine,
      open,
      draft,
      sessionRevision,
      modal,
      reservedWidth,
      transition,
      reducedMotion,
      openOrderTicket,
      closeOrderTicket,
      toggleOrderTicket,
    }),
    [
      closeOrderTicket,
      draft,
      engine,
      modal,
      open,
      openOrderTicket,
      reducedMotion,
      reservedWidth,
      sessionRevision,
      toggleOrderTicket,
      transition,
    ],
  );
};
